/**
 * Dates and times of the agenda screens (decision 0022), always read in the agenda's time zone,
 * never the browser's: a day is "YYYY-MM-DD", a time "HH:MM". Pure, so the week layout and the
 * slot grid are tested without a browser.
 */
const DAY_MS = 86_400_000;

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** The wall clock of an instant in a time zone. */
export function wallClock(at: Date | string, timeZone: string): WallClock {
  const out: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(at)))
    if (p.type !== "literal") out[p.type] = Number(p.value);
  return {
    year: out.year!,
    month: out.month!,
    day: out.day!,
    hour: out.hour === 24 ? 0 : out.hour!,
    minute: out.minute!,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoDay = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** The calendar day of an instant in a time zone. */
export function dayOf(at: Date | string, timeZone: string): string {
  const w = wallClock(at, timeZone);
  return isoDay(w.year, w.month, w.day);
}

/** Minutes since midnight of an instant in a time zone. */
export function minuteOfDay(at: Date | string, timeZone: string): number {
  const w = wallClock(at, timeZone);
  return w.hour * 60 + w.minute;
}

function parseDay(day: string): { y: number; m: number; d: number } {
  const [y, m, d] = day.split("-").map(Number);
  return { y: y!, m: m!, d: d! };
}

export function addDays(day: string, n: number): string {
  const { y, m, d } = parseDay(day);
  const next = new Date(Date.UTC(y, m - 1, d + n));
  return isoDay(
    next.getUTCFullYear(),
    next.getUTCMonth() + 1,
    next.getUTCDate(),
  );
}

/** 0 is Monday, 6 is Sunday (weeks start on Monday, as in Brazil's calendars). */
export function weekdayOf(day: string): number {
  const { y, m, d } = parseDay(day);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function mondayOf(day: string): string {
  return addDays(day, -weekdayOf(day));
}

export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** Whole days from `a` to `b`. */
export function daysBetween(a: string, b: string): number {
  const p = parseDay(a);
  const q = parseDay(b);
  return Math.round(
    (Date.UTC(q.y, q.m - 1, q.d) - Date.UTC(p.y, p.m - 1, p.d)) / DAY_MS,
  );
}

function offsetMinutes(at: Date, timeZone: string): number {
  const w = wallClock(at, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);
  return Math.round(
    (asUtc - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000,
  );
}

/** The instant of a wall-clock day and time in a time zone ("2026-10-25", "10:30"). */
export function instantOf(day: string, time: string, timeZone: string): Date {
  const { y, m, d } = parseDay(day);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh ?? 0, mm ?? 0);
  let at = new Date(guess - offsetMinutes(new Date(guess), timeZone) * 60_000);
  // Across a change of offset (summer time), the second pass lands on the right side.
  const again = new Date(guess - offsetMinutes(at, timeZone) * 60_000);
  if (again.getTime() !== at.getTime()) at = again;
  return at;
}

export function todayIn(timeZone: string, now: Date = new Date()): string {
  return dayOf(now, timeZone);
}

/** The browser's own zone, for the hint when the agenda keeps another one. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export interface Span {
  id: string;
  /** Minutes since the day's midnight. */
  start: number;
  end: number;
}
export interface Placed {
  id: string;
  /** Column of the event inside its group of overlapping events, and how many columns it has. */
  col: number;
  cols: number;
}

/**
 * Overlapping events of a day side by side, as calendar apps do: each group of events that touch
 * one another shares the width, and every event takes the first free column.
 */
export function layoutDay(spans: Span[]): Placed[] {
  const sorted = [...spans].sort(
    (a, b) =>
      a.start - b.start ||
      b.end - b.start - (a.end - a.start) ||
      a.id.localeCompare(b.id),
  );
  const out: Placed[] = [];
  let group: Array<{ id: string; col: number }> = [];
  let columnsEnd: number[] = [];
  let groupEnd = -Infinity;
  const flush = () => {
    const cols = columnsEnd.length;
    for (const g of group) out.push({ id: g.id, col: g.col, cols });
    group = [];
    columnsEnd = [];
  };
  for (const s of sorted) {
    const end = Math.max(s.end, s.start + 1);
    if (s.start >= groupEnd) {
      flush();
      groupEnd = -Infinity;
    }
    let col = columnsEnd.findIndex((e) => e <= s.start);
    if (col === -1) {
      col = columnsEnd.length;
      columnsEnd.push(end);
    } else columnsEnd[col] = end;
    group.push({ id: s.id, col });
    groupEnd = Math.max(groupEnd, end);
  }
  flush();
  return out;
}

/**
 * The part of an event inside a day, in minutes since that day's midnight (an event crossing
 * midnight shows in both days). Null when it does not touch the day.
 */
export function spanInDay(
  start: string,
  end: string,
  day: string,
  timeZone: string,
): { start: number; end: number } | null {
  const dayStart = instantOf(day, "00:00", timeZone).getTime();
  const dayEnd = instantOf(addDays(day, 1), "00:00", timeZone).getTime();
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (e <= dayStart || s >= dayEnd) return null;
  const length = (dayEnd - dayStart) / 60_000;
  return {
    start: Math.max(0, Math.round((s - dayStart) / 60_000)),
    end: Math.min(length, Math.round((e - dayStart) / 60_000)),
  };
}

/** Items grouped by their day in the time zone, days in order. */
export function groupByDay<T>(
  items: T[],
  at: (item: T) => string,
  timeZone: string,
): Array<{ day: string; items: T[] }> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const day = dayOf(at(item), timeZone);
    const list = map.get(day);
    if (list) list.push(item);
    else map.set(day, [item]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, list]) => ({ day, items: list }));
}

/** Where to scroll the week first: an hour before the earliest event, else the working morning. */
export function firstHour(startMinutes: number[], fallback = 8): number {
  if (!startMinutes.length) return fallback;
  return Math.max(
    0,
    Math.min(fallback, Math.floor(Math.min(...startMinutes) / 60) - 1),
  );
}
